import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import s from './Lookbooks.module.css';
import { assistantAxiosInstance, fittingAxiosInstance } from "../../../api";
import { ArrowUpRight, X, ChevronDown, ChevronUp } from 'lucide-react';

interface FashionFeedItem {
    collageUrl: string;
    styleName: string;
    styleId: number;
    fashionGuid: string;
    isFavorite: boolean;
    isWardrobe: boolean;
}

interface FashionFeedResponse {
    countRecords: number;
    items: FashionFeedItem[];
}

interface FittingHistory {
    fittingPhotoUrl: string;
    fashionGuid: string;
}

interface FittingHistoryResponse {
    countResults: number;
    fittingHistoryList: FittingHistory[];
}

interface LookbookItem {
    id: string;
    title: string;
    image: string;
    fashionGuid: string;
}

interface FashionCompositionItem {
    id: number;
    name: string;
    category: string;
    thumbUrl: string;
    cost: number;
    manufacturer: string;
    variantId: null | number;
    clothLayer: string;
    isFavorite: boolean;
    isWardrobe: boolean;
    colorCloth: string;
    isBasisOfCollage: boolean;
}

interface DetailsCollageInfo {
    collage: string;
    event: null | string;
    styleName: string;
    styleId: number;
    fittingPhotoUrl: null | string;
    totalCost: number;
    fashionGuid: string;
    isFavorite: null | boolean;
    commentStylist: null | string;
    adviceStylist: null | string;
    fashionComposition: FashionCompositionItem[];
    status: string;
}

type TabType = 'feed' | 'fittings' | 'wardrobe';

const TABS: { key: TabType; label: string }[] = [
    { key: 'feed', label: 'Лента' },
    { key: 'fittings', label: 'Примерки' },
    { key: 'wardrobe', label: 'Образы с вещами из шкафа' },
];

const Lookbooks: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const [items, setItems] = useState<LookbookItem[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(10);
    const [activeTab, setActiveTab] = useState<TabType>('feed');

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalImages, setModalImages] = useState<DetailsCollageInfo[]>([]);
    const [modalLoading, setModalLoading] = useState<boolean>(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

    const getFeedHistory = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await assistantAxiosInstance.get<FashionFeedResponse>(
                'mobile/feed/history',
                {
                    params: {
                        currentPage,
                        itemsPerPage: 8,
                        userId,
                    }
                }
            );

            const mapped: LookbookItem[] = response.data.items.map(item => ({
                id: item.fashionGuid,
                title: item.styleName || 'Без названия',
                image: item.collageUrl,
                fashionGuid: item.fashionGuid,
            }));

            setItems(mapped);
            setTotalCount(response.data.countRecords);
        } catch (err) {
            setError('Ошибка при загрузке ленты');
        } finally {
            setLoading(false);
        }
    }, [currentPage, userId]);

    const getFittingHistory = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fittingAxiosInstance.get<FittingHistoryResponse>(
                'fittingHistory',
                {
                    params: {
                        currentPage,
                        itemsPerPage: 8,
                        userId,
                    }
                }
            );

            const mapped: LookbookItem[] = response.data.fittingHistoryList.map(item => ({
                id: item.fashionGuid,
                title: 'Примерка',
                image: item.fittingPhotoUrl,
                fashionGuid: item.fashionGuid,
            }));

            setItems(mapped);
            setTotalCount(response.data.countResults);
        } catch (err) {
            setError('Ошибка при загрузке примерок');
        } finally {
            setLoading(false);
        }
    }, [currentPage, userId]);

    const loadData = useCallback(() => {
        switch (activeTab) {
            case 'feed':
                getFeedHistory();
                break;
            case 'fittings':
                getFittingHistory();
                break;
            case 'wardrobe':
                setItems([]);
                setTotalCount(0);
                setLoading(false);
                break;
        }
    }, [activeTab, getFeedHistory, getFittingHistory]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
        setCurrentPage(1);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const totalPages = Math.ceil(totalCount / itemsPerPage);

    const handleOpenModal = async (item: LookbookItem) => {
        setIsModalOpen(true);
        setModalLoading(true);
        setModalError(null);
        setExpandedItems(new Set());

        try {
            const response = await assistantAxiosInstance.get<DetailsCollageInfo[]>(
                'mobile/assistant/detailsFashionHistory',
                {
                    params: {
                        fashionGuid: item.fashionGuid,
                        withFitting: true,
                    }
                }
            );
            setModalImages(response.data);
        } catch (err) {
            setModalError('Ошибка при загрузке деталей образа');
        } finally {
            setModalLoading(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setModalImages([]);
        setExpandedItems(new Set());
    };

    const toggleComposition = (index: number) => {
        setExpandedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const renderTabs = () => (
        <div className={s.tabs}>
            {TABS.map(tab => (
                <button
                    key={tab.key}
                    className={`${s.tab} ${activeTab === tab.key ? s.tabActive : ''}`}
                    onClick={() => handleTabChange(tab.key)}
                >
                    {tab.label}
                </button>
            ))}
        </div>
    );

    if (loading) {
        return (
            <div className={s.container}>
                <div className={s.header}>{renderTabs()}</div>
                <div className={s.loaderWrapper}>
                    <div className={s.spinner} />
                    <p>Загрузка...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={s.container}>
                <div className={s.header}>{renderTabs()}</div>
                <div className={s.error}>{error}</div>
            </div>
        );
    }

    return (
        <div className={s.container}>
            <div className={s.header}>
                {renderTabs()}
                <span className={s.count}>Всего: {totalCount}</span>
            </div>

            {items.length === 0 ? (
                <p className={s.empty}>
                    {activeTab === 'fittings' ? 'Нет примерок' : 'Нет образов'}
                </p>
            ) : (
                <>
                    <div className={s.grid}>
                        {items.map((item) => (
                            <div key={item.id}>
                                <article
                                    className={s.lookCard}
                                    onClick={() => handleOpenModal(item)}
                                >
                                    <div className={s.imageWrapper}>
                                        <img src={item.image} alt={item.title} />
                                    </div>
                                    <div className={s.lookInfo}>
                                        <div>
                                            <h3 className={s.lookTitle}>{item.title}</h3>
                                        </div>
                                        <span className={s.arrowIcon}>
                                            <ArrowUpRight size={16} />
                                        </span>
                                    </div>
                                </article>
                            </div>
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className={s.pagination}>
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={s.pageButton}
                            >
                                ← Назад
                            </button>
                            <span className={s.pageInfo}>
                                Страница {currentPage} из {totalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className={s.pageButton}
                            >
                                Вперед →
                            </button>
                        </div>
                    )}
                </>
            )}

            {isModalOpen && (
                <div className={s.modalOverlay} onClick={handleCloseModal}>
                    <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
                        <button className={s.modalClose} onClick={handleCloseModal}>
                            <X size={24} />
                        </button>

                        {modalLoading ? (
                            <div className={s.modalLoader}>
                                <div className={s.spinner} />
                                <p>Загрузка образов...</p>
                            </div>
                        ) : modalError ? (
                            <div className={s.modalError}>{modalError}</div>
                        ) : modalImages.length > 0 ? (
                            <>
                                <div className={s.modalHeader}>
                                    <h2 className={s.modalTitle}>
                                        {modalImages[0]?.styleName || 'Подборка образов'}
                                    </h2>
                                    <span className={s.modalCount}>
                                        {modalImages.length} образов
                                    </span>
                                </div>

                                <div className={s.modalGrid}>
                                    {modalImages.map((item, index) => (
                                        <div key={index} className={s.modalCard}>
                                            <div className={s.modalImageWrapper}>
                                                <img
                                                    src={item.collage}
                                                    alt={`Образ ${index + 1}`}
                                                    className={s.modalImage}
                                                />
                                            </div>
                                            <div className={s.modalCardInfo}>
                                                <p className={s.modalCardStyle}>
                                                    {item.styleName || 'Без названия'}
                                                </p>
                                                <p className={s.modalCardPrice}>
                                                    {item.totalCost.toLocaleString()} ₽
                                                </p>
                                            </div>

                                            {item.fashionComposition && item.fashionComposition.length > 0 && (
                                                <button
                                                    className={s.compositionToggle}
                                                    onClick={() => toggleComposition(index)}
                                                >
                                                    <span>Состав образа</span>
                                                    {expandedItems.has(index) ? (
                                                        <ChevronUp size={16} />
                                                    ) : (
                                                        <ChevronDown size={16} />
                                                    )}
                                                </button>
                                            )}

                                            {expandedItems.has(index) && item.fashionComposition && (
                                                <div className={s.modalCardComposition}>
                                                    <div className={s.compositionGrid}>
                                                        {item.fashionComposition.map((cloth) => (
                                                            <Link
                                                                key={cloth.id}
                                                                to={`/details/${cloth.id}`}
                                                                target="_blank"
                                                                className={s.compositionClothLink}
                                                            >
                                                                <div className={s.compositionCloth}>
                                                                    <img
                                                                        src={cloth.thumbUrl}
                                                                        alt={cloth.name}
                                                                        className={s.compositionClothImage}
                                                                    />
                                                                    <div className={s.compositionClothInfo}>
                                                                        <p className={s.compositionClothName}>
                                                                            {cloth.name}
                                                                        </p>
                                                                        <p className={s.compositionClothBrand}>
                                                                            {cloth.manufacturer}
                                                                        </p>
                                                                        <p className={s.compositionClothPrice}>
                                                                            {cloth.cost.toLocaleString()} ₽
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className={s.modalEmpty}>Нет изображений</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Lookbooks;